'use client';

import classNames from 'classnames';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useController, useForm, useWatch } from 'react-hook-form';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import { getCustomer } from '@/components/blocks/simple-customer/simple-customer.utils';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Checkbox from '@/components/molecules/checkbox/checkbox';
import Input from '@/components/molecules/input/input';
import { EditedPassenger } from '@/components/organisms/edit-pax-details/edit-pax-details';
import ShowMore from '@/components/utilities/show-more/show-more';
import useMutationUpdatePreBookingCustomer from '@/integrations/booking/pre-booking/customers/update-customer/update-customer.hooks';
import useMutationUpdatePreBookingPassengers from '@/integrations/booking/pre-booking/passengers/update-passengers/update-passengers.hooks';
import useQuerySimpleCustomer from '@/integrations/content/content-modules/simple-customer/simple-customer.hooks';
import { NAME_DEPENDENT_QUERIES, QueryId } from '@/integrations/content/utils';
import { trackEmailAddedEvent } from '@/integrations/tracking/google-tag-manager/scripts';
import { useBookingFlowContext } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useDebounce from '@/libraries/hooks/use-debounce';
import isDeepEqual from '@/libraries/utils/is-deep-equal';
import isEmptyObject from '@/libraries/utils/is-empty-object';
import validateEmail from '@/libraries/validators/validate-email';
import styles from './simple-customer.module.scss';

export interface SimpleCustomerProps {
  /** The text to show as a headline */
  headline?: Content.Components['SimpleCustomerHeadlessModuleDto']['headline'];
  /** The UUID of the module */
  id: Content.Components['SimpleCustomerHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['SimpleCustomerHeadlessModuleDto']['pageId'];
  /** The parameters for the booking */
  parameters: Content.Components['SimpleCustomerHeadlessModuleDto']['parameters'];
}

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  newsletter: boolean;
}

export interface EditedCustomer {
  originalFields: Partial<Content.Properties.Customer & { age?: number }>;
  updatedFields: Partial<Content.Properties.Customer & { age?: number }>;
}

const DEBOUNCE_DELAY = 1000;

const SimpleCustomer = ({
  headline,
  id,
  pageId,
  parameters,
}: SimpleCustomerProps) => {
  const { status: sessionStatus } = useSession();
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const {
    activeAnchor,
    anchorSignal,
    moduleStatusMap,
    registerNavigationBlocker,
    setActiveAnchor,
    updateStepData,
    updateModuleStatus,
  } = useBookingFlowContext();
  const isFormInitialized = useRef(false);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const lastUpdatedPassengerFields = useRef<Record<string, unknown>>({});
  const [customer, setCustomer] = useState<Content.Properties.Customer>();
  const [editedCustomer, setEditedCustomer] = useState<EditedCustomer>({
    originalFields: {},
    updatedFields: {},
  });
  const hasNoUpdates =
    isEmptyObject(editedCustomer.originalFields) &&
    isEmptyObject(editedCustomer.updatedFields);

  const { value: debouncedEditedCustomer, isPending } = useDebounce(
    editedCustomer,
    DEBOUNCE_DELAY,
    !hasNoUpdates,
  );
  const identifiers = { ...parameters, id, pageId };

  const {
    data: dataSimpleCustomer,
    isLoading: isLoadingSimpleCustomer,
    refetch: refetchSimpleCustomer,
  } = useQuerySimpleCustomer({
    parameters: identifiers,
  });

  const { mutate: mutateUpdatePreBookingCustomer } =
    useMutationUpdatePreBookingCustomer();

  const { mutate: mutateUpdatePreBookingPassengers } =
    useMutationUpdatePreBookingPassengers();

  const defaultValues = {
    email: '',
    firstName: '',
    lastName: '',
    newsletter: false,
  };

  const { control, formState, register, reset, watch } = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: 'onBlur',
  });

  const formValues = watch();
  const watchedFirstName = useWatch({ control, name: 'firstName' });
  const watchedLastName = useWatch({ control, name: 'lastName' });
  const watchedEmail = useWatch({ control, name: 'email' });

  const {
    field: {
      value: newsletterValue,
      onChange: onNewsletterChange,
      ref: newsletterRef,
      name: newsletter,
    },
  } = useController({
    control,
    name: 'newsletter',
  });

  const handleTermsOfServiceChange = () => {
    onNewsletterChange(!newsletterValue);
  };

  const shouldHighlightErrors = useMemo(() => {
    return (
      activeAnchor === id &&
      !moduleStatusMap[id]?.isComplete &&
      moduleStatusMap[id]?.isRequired
    );
  }, [activeAnchor, id, moduleStatusMap]);

  const getErrorMessage = (
    fieldName: keyof FormValues,
    required?: boolean,
  ): string | undefined => {
    if (shouldHighlightErrors && !formValues[fieldName] && required) {
      return t('items.form.validationRequired');
    }

    const error = formState.errors[fieldName];

    if (!error) return;

    return error.message;
  };

  const getLoadingState = () => {
    return (
      <React.Fragment>
        <div className={styles.inputs}>
          <SkeletonLoader
            className={classNames(styles.input, styles.loader)}
            height="52px"
          />

          <SkeletonLoader
            className={classNames(styles.input, styles.loader)}
            height="52px"
          />

          <SkeletonLoader
            className={classNames(styles.input, styles.loader)}
            height="52px"
          />
        </div>

        <div className={styles.termsOfService}>
          <SkeletonLoader
            className={classNames(styles.radioButton, styles.loader)}
            height="45px"
          />
        </div>
      </React.Fragment>
    );
  };

  useEffect(() => {
    if (!isFormInitialized.current) return;

    if (!customer) return;

    setEditedCustomer(previous => {
      const originalFields: Partial<Content.Properties.Customer> = {
        ...previous.originalFields,
      };

      const updatedFields: Partial<Content.Properties.Customer> = {
        ...previous.updatedFields,
      };

      for (const field of Object.keys(
        formValues,
      ) as (keyof Content.Properties.Customer)[]) {
        const currentValue = formValues[field];
        const originalValue = customer[field];

        if (currentValue !== originalValue) {
          updatedFields[field] = currentValue;
          originalFields[field] = originalValue as any;
        } else {
          delete updatedFields[field];
        }
      }

      if (isDeepEqual(previous, { originalFields, updatedFields })) {
        return previous;
      }

      return {
        originalFields,
        updatedFields,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  useEffect(() => {
    setActiveAnchor(null);
    updateModuleStatus(id, {
      isComplete: formState.isValid,
      isRequired: moduleStatusMap[id]?.isRequired,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isValid]);

  useEffect(() => {
    updateStepData('trip', 'customerFirstName', watchedFirstName);
    updateStepData('trip', 'customerLastName', watchedLastName);
    updateStepData('trip', 'customerEmail', watchedEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFirstName, watchedLastName, watchedEmail]);

  useEffect(() => {
    if (!customer) return;

    if (!isFormInitialized.current) return;

    const { originalFields, updatedFields } = debouncedEditedCustomer;
    const mergedFields = { ...originalFields, ...updatedFields };
    const hasChanges = !isEmptyObject(mergedFields);

    if (!hasChanges) return;

    const editedCustomerPayload: Omit<EditedCustomer, 'originalFields'> = {
      updatedFields: mergedFields,
    };

    const updateCustomerInvalidationKeys = [QueryId.CUSTOMER_SUMMARY];

    mutateUpdatePreBookingCustomer({
      brand,
      customer: editedCustomerPayload,
      invalidationKeys: updateCustomerInvalidationKeys,
      parameters: identifiers,
    });

    const travelPlannerId = customer.uniquePaxHash;

    if (!travelPlannerId) return;

    // Relevant fields in order to update the passenger
    const keysToCheck = ['firstName', 'lastName', 'email'];

    // Get updated snapshot of relevant passenger fields
    const updatedPassengerFields = Object.fromEntries(
      keysToCheck
        .map(key => [key, mergedFields[key]])
        .filter(([, value]) => value !== undefined),
    );

    // Compare to the last sent
    const shouldUpdatePassengers = keysToCheck.some(key => {
      return (
        lastUpdatedPassengerFields.current[key] !== updatedPassengerFields[key]
      );
    });

    if (!shouldUpdatePassengers) return;

    const hasFirstNameOrLastNameChange =
      lastUpdatedPassengerFields.current?.firstName !==
        updatedPassengerFields?.firstName ||
      lastUpdatedPassengerFields.current?.lastName !==
        updatedPassengerFields?.lastName;

    const editedPassengersPayload: Omit<
      EditedPassenger,
      'originalFields' | 'additionalFields'
    >[] = [
      {
        uniquePaxHash: travelPlannerId,
        updatedFields: updatedPassengerFields,
      },
    ];

    const { email } = debouncedEditedCustomer.updatedFields;

    if (email && validateEmail.test(email)) {
      trackEmailAddedEvent({
        user_data: { email },
      });
    }

    const updatePassengersInvalidationKeys = [
      QueryId.SIMPLE_PASSENGER,
      QueryId.DETAILED_PASSENGER,
      ...(hasFirstNameOrLastNameChange ? NAME_DEPENDENT_QUERIES : []),
    ];

    mutateUpdatePreBookingPassengers({
      brand,
      invalidationKeys: updatePassengersInvalidationKeys,
      parameters: identifiers,
      passengers: editedPassengersPayload,
    });

    lastUpdatedPassengerFields.current = updatedPassengerFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEditedCustomer]);

  useEffect(() => {
    if (!customer) return;

    reset({
      email: customer.email,
      firstName: customer.firstName,
      lastName: customer.lastName,
      newsletter: customer.newsletter,
    });

    setEditedCustomer({
      originalFields: {},
      updatedFields: {},
    });

    isFormInitialized.current = true;

    return () => {
      isFormInitialized.current = false;
      lastUpdatedPassengerFields.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    if (!dataSimpleCustomer) return;

    setActiveAnchor(null);
    setCustomer(getCustomer(dataSimpleCustomer));
    updateModuleStatus(id, {
      isComplete: dataSimpleCustomer.isComplete,
      isRequired: dataSimpleCustomer.isRequired,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSimpleCustomer]);

  useEffect(() => {
    if (!componentRef.current) return;

    if (!activeAnchor) return;

    if (activeAnchor === id) {
      componentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [activeAnchor, anchorSignal, id]);

  // Automatically block/unblock next step navigation
  useEffect(() => {
    registerNavigationBlocker(`simple-customer`, isPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  useEffect(() => {
    refetchSimpleCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={componentRef} className={styles.component}>
      <div className={styles.headlineWrapper}>
        {headline && (
          <Typography className={styles.headline} tagStyle="headingSmall">
            {headline}
          </Typography>
        )}

        {sessionStatus === 'unauthenticated' &&
          (dataSimpleCustomer?.existingUserText ||
            dataSimpleCustomer?.loginLink) && (
            <div className={styles.existingUser}>
              {dataSimpleCustomer?.existingUserText && (
                <Typography tagStyle="bodyMedium">
                  {dataSimpleCustomer.existingUserText}
                </Typography>
              )}

              {dataSimpleCustomer?.loginLink && (
                <AnchorLink {...dataSimpleCustomer.loginLink} />
              )}
            </div>
          )}

        {shouldHighlightErrors && (
          <Typography
            className={styles.error}
            text={`*${t('items.modules.bookingFlow.required')}`}
          />
        )}
      </div>

      {isLoadingSimpleCustomer && getLoadingState()}

      {!isLoadingSimpleCustomer && (
        <div className={styles.inputs}>
          <Input
            {...register('firstName', { required: true })}
            className={styles.input}
            error={getErrorMessage('firstName', true)}
            id="firstName"
            label={t('items.modules.bookingFlow.firstName')}
            required
          />

          <Input
            {...register('lastName', { required: true })}
            className={styles.input}
            error={getErrorMessage('lastName', true)}
            id="lastName"
            label={t('items.modules.bookingFlow.lastName')}
            required
          />

          <Input
            {...register('email', {
              pattern: {
                message: t('items.modules.bookingFlow.validationEmail'),
                value: validateEmail,
              },
              required: true,
            })}
            className={styles.input}
            error={getErrorMessage('email', true)}
            id="email"
            label={t('items.modules.bookingFlow.email')}
            required
            type="email"
          />
        </div>
      )}

      {dataSimpleCustomer?.termsOfServiceText && (
        <div className={styles.termsOfService}>
          <ShowMore className={styles.showMore} gap="small" height={70}>
            <Checkbox
              ref={newsletterRef}
              centerAlignCheckbox={false}
              checked={newsletterValue}
              className={styles.radioButton}
              error={getErrorMessage(
                'newsletter',
                dataSimpleCustomer?.termsOfServiceRequired,
              )}
              name={newsletter}
              onChange={handleTermsOfServiceChange}
              required={dataSimpleCustomer?.termsOfServiceRequired}
            >
              <RichText text={dataSimpleCustomer?.termsOfServiceText} />
            </Checkbox>
          </ShowMore>
        </div>
      )}
    </div>
  );
};

SimpleCustomer.displayName = 'SimpleCustomer';

export default SimpleCustomer;
